<template>
  <div class="main-conent main-conent-maxheight main-conent-bgFFF pest-knowledge">
    <!-- 用药提醒（来源：害虫防治.docx） -->
    <el-alert
      class="pk-notice"
      type="warning"
      show-icon
      :closable="false"
      title="用药提醒：针对不同螟虫的抗药性现状，必须科学选药、轮换用药，避免盲目加大剂量"
      :description="controlNote"
    ></el-alert>

    <!-- 工具栏：搜索 + 分类筛选 -->
    <div class="pk-toolbar">
      <el-input
        v-model="keyword"
        class="pk-toolbar__search"
        prefix-icon="el-icon-search"
        placeholder="搜索害虫名称、学名或寄主作物"
        clearable
      ></el-input>

      <el-radio-group v-model="category" class="pk-toolbar__category">
        <el-radio-button label="全部">全部</el-radio-button>
        <el-radio-button
          v-for="item in pestCategories"
          :key="item"
          :label="item"
          >{{ item }}</el-radio-button
        >
      </el-radio-group>
    </div>

    <div class="pk-summary">共收录 {{ filteredPests.length }} 种害虫</div>

    <!-- 害虫卡片列表 -->
    <div v-if="filteredPests.length" class="pk-grid">
      <el-card
        v-for="pest in filteredPests"
        :key="pest.id"
        class="pk-card"
        shadow="hover"
        @click.native="openDetail(pest)"
      >
        <div class="pk-card__image">
          <img v-if="pest.image" :src="pest.image" :alt="pest.name" />
          <div v-else class="pk-card__image-fallback">
            <svg-icon icon-class="bug"></svg-icon>
            <span>样本图待补充</span>
          </div>
        </div>
        <div class="pk-card__body">
          <div class="pk-card__name">{{ pest.name }}</div>
          <div class="pk-card__alias">{{ pest.alias }}</div>
          <div class="pk-card__tags">
            <el-tag
              v-if="pest.level"
              size="mini"
              :type="levelType(pest.level)"
              >{{ pest.level }}</el-tag
            >
            <el-tag
              v-for="cat in pest.categories"
              :key="cat"
              size="mini"
              type="primary"
              >{{ cat }}</el-tag
            >
            <el-tag size="mini" type="info">{{ pest.host }}</el-tag>
          </div>
        </div>
        <div class="pk-card__footer">
          <span class="pk-card__action">查看详情</span>
          <i class="el-icon-arrow-right"></i>
        </div>
      </el-card>
    </div>

    <el-empty
      v-else
      description="未找到相关害虫，请更换关键词或分类"
    ></el-empty>

    <!-- 详情抽屉 -->
    <el-drawer
      :visible.sync="drawerVisible"
      :title="currentPest ? currentPest.name : ''"
      size="560px"
      custom-class="pk-drawer"
    >
      <div v-if="currentPest" class="pk-detail">
        <div class="pk-detail__image">
          <img
            v-if="currentPest.image"
            :src="currentPest.image"
            :alt="currentPest.name"
          />
          <div v-else class="pk-detail__image-fallback">
            <svg-icon icon-class="bug"></svg-icon>
            <span>样本图待补充</span>
          </div>
        </div>

        <div class="pk-detail__basic">
          <div class="pk-detail__scientific">
            {{ currentPest.scientificName }}
          </div>
          <div class="pk-detail__tags">
            <el-tag
              v-if="currentPest.level"
              size="small"
              :type="levelType(currentPest.level)"
              >{{ currentPest.level }}</el-tag
            >
            <el-tag
              v-for="cat in currentPest.categories"
              :key="cat"
              size="small"
              type="primary"
              >{{ cat }}</el-tag
            >
            <el-tag size="small" type="info">{{ currentPest.alias }}</el-tag>
          </div>
        </div>

        <div class="pk-detail__section">
          <div class="pk-detail__section-title">
            <i class="el-icon-crop"></i>寄主作物
          </div>
          <p class="pk-detail__text">{{ currentPest.host }}</p>
        </div>

        <div class="pk-detail__section">
          <div class="pk-detail__section-title">
            <i class="el-icon-location-outline"></i>主要危害地区
          </div>
          <div class="pk-detail__tags">
            <el-tag
              v-for="region in currentPest.regions"
              :key="region"
              size="small"
              type="warning"
              effect="plain"
              >{{ region }}</el-tag
            >
          </div>
        </div>

        <div class="pk-detail__section">
          <div class="pk-detail__section-title">
            <i class="el-icon-warning-outline"></i>危害症状
          </div>
          <p class="pk-detail__text">{{ currentPest.hazard }}</p>
        </div>

        <div class="pk-detail__section">
          <div class="pk-detail__section-title">
            <i class="el-icon-time"></i>发生规律
          </div>
          <p class="pk-detail__text">{{ currentPest.regular }}</p>
        </div>

        <div class="pk-detail__section">
          <div class="pk-detail__section-title">
            <i class="el-icon-first-aid-kit"></i>防治方法
          </div>
          <p class="pk-detail__text">{{ currentPest.control }}</p>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { pestList, pestCategories, controlNote } from './data'

export default {
  name: 'PestKnowledge',
  data() {
    return {
      keyword: '',
      category: '全部',
      drawerVisible: false,
      currentPest: null,
      pestList,
      pestCategories,
      controlNote
    }
  },
  computed: {
    filteredPests() {
      const keyword = this.keyword.trim().toLowerCase()
      return this.pestList.filter(pest => {
        const matchCategory =
          this.category === '全部' || pest.categories.includes(this.category)
        if (!matchCategory) {
          return false
        }
        if (!keyword) {
          return true
        }
        return [pest.name, pest.alias, pest.scientificName, pest.host].some(
          field => String(field).toLowerCase().includes(keyword)
        )
      })
    }
  },
  methods: {
    levelType(level) {
      const map = { 一类: 'danger', 二类: 'warning', 其他: 'info' }
      return map[level] || 'info'
    },
    openDetail(pest) {
      this.currentPest = pest
      this.drawerVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.pest-knowledge {
  padding: 16px;
  overflow: auto;
  box-sizing: border-box;
}

.pk-notice {
  margin-bottom: 16px;
}

.pk-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fff;

  &__search {
    width: 320px;
  }

  &__category {
    margin-top: 8px;
  }
}

.pk-summary {
  padding: 12px 4px;
  color: #909399;
  font-size: 13px;
}

.pk-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.pk-card {
  cursor: pointer;

  ::v-deep .el-card__body {
    padding: 0;
  }
}

.pk-card__image {
  height: 150px;
  background-color: #f5f7fa;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
}

.pk-card__image-fallback {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 13px;

  .svg-icon {
    font-size: 40px;
    margin-bottom: 8px;
  }
}

.pk-card__body {
  padding: 14px 16px 4px;
}

.pk-card__name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.pk-card__alias {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pk-card__tags {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  .el-tag {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.pk-card__footer {
  padding: 10px 16px 14px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: #409eff;
  font-size: 13px;
}

.pk-detail {
  padding: 0 20px 20px;
}

.pk-detail__image {
  margin: 0 -20px 16px;
  padding: 12px 0;
  background-color: #f5f7fa;
  text-align: center;

  img {
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
    display: inline-block;
  }
}

.pk-detail__image-fallback {
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 13px;

  .svg-icon {
    font-size: 48px;
    margin-bottom: 8px;
  }
}

.pk-detail__basic {
  margin-bottom: 20px;
}

.pk-detail__scientific {
  font-size: 14px;
  color: #606266;
  font-style: italic;
}

.pk-detail__tags {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pk-detail__section {
  margin-bottom: 20px;

  &-title {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;

    i {
      margin-right: 6px;
      color: #409eff;
    }
  }
}

.pk-detail__text {
  margin: 0;
  line-height: 1.8;
  font-size: 13px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
